import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Car, MapPin, Shield, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import AvailabilityHintPill from "@/components/AvailabilityHintPill";
import { FavoriteButton } from "@/components/FavoriteButton";
import { AverageRating } from "@/components/AverageRating";
import { maskAddress } from "@/lib/addressMasking";
import listingPlaceholder from "@/assets/listing-placeholder.svg";

interface ParkingSpotCardProps {
  id: string;
  title: string;
  address: string;
  price: number;
  size: "S" | "M" | "L" | "XL";
  hasCharger: boolean;
  isCovered: boolean;
  photos: string[];
  distance?: string;
}

export default function ParkingSpotCard({
  id,
  title,
  address,
  price,
  size,
  hasCharger,
  isCovered,
  photos,
  distance,
}: ParkingSpotCardProps) {
  const sizeLabels = {
    S: "Lille",
    M: "Medium",
    L: "Stor",
    XL: "Ekstra Stor",
  };

  // Mask address for security - only show area/postcode before booking
  const displayAddress = maskAddress(address);

  return (
    <Card className="group overflow-hidden border-border/70 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl">
      <Link to={`/spot/${id}`} className="block focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
      <div className="aspect-[4/3] relative overflow-hidden bg-muted">
        <img
          src={photos?.[0] || listingPlaceholder}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-foreground/60 to-transparent" />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {hasCharger && (
            <Badge variant="secondary" className="bg-background/90 text-foreground backdrop-blur-sm">
              <Zap className="h-3 w-3 mr-1" />
              El-lader
            </Badge>
          )}
          {isCovered && (
            <Badge variant="secondary" className="bg-background/90 text-foreground backdrop-blur-sm">
              <Shield className="h-3 w-3 mr-1" />
              Overdækket
            </Badge>
          )}
        </div>
        <div className="absolute top-3 right-3 flex gap-2">
          {distance && (
            <Badge variant="outline" className="bg-background/90">
              {distance}
            </Badge>
          )}
          <FavoriteButton listingId={id} />
        </div>
      </div>
      </Link>

      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1">
            <Link to={`/spot/${id}`} className="font-semibold text-foreground line-clamp-1 hover:text-primary">
              {title}
            </Link>
            <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground line-clamp-1">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              {displayAddress}
            </p>
          </div>
          <div className="text-right leading-tight">
            <div className="text-lg font-bold text-foreground">{price} kr</div>
            <div className="text-xs text-muted-foreground">pr. dag</div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <AverageRating listingId={id} />

            <Badge variant="outline" className="text-xs">
              <Car className="h-3 w-3 mr-1" />
              {sizeLabels[size]}
            </Badge>

            {/* NYT: lille availability-hint (viser kun noget hvis der findes availability-rækker) */}
            <AvailabilityHintPill listingId={id} />
          </div>

          <Button size="sm" variant="ghost" asChild className="shrink-0 px-2">
            <Link to={`/spot/${id}`} aria-label={`Se detaljer for ${title}`}><ArrowRight className="h-4 w-4" /></Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
